/* © NXAPP Limited */
import { useRef, useEffect, useCallback, useState } from 'react'
import {
  Bold, Italic, Underline, List, ListOrdered,
  Link2, Heading2, Undo2, Redo2, Eraser, Tag, PenTool, Eye, Pencil, Image as ImageIcon,
} from 'lucide-react'
import { PLAIN_SIGNATURE_HTML } from './plainStarter'
import { supabase } from '@/lib/supabase'

export interface PreviewIdentity {
  companyName: string
  companyAddress: string
  companyWebsite: string
}

interface Props {
  initialHtml?: string
  /** Values from the resolved sending profile — used for preview only. */
  identity?: PreviewIdentity
  onChange: (html: string) => void
  /** Hide the built-in Preview toggle (e.g. when a live preview is shown alongside). */
  hidePreview?: boolean
}

/** Wrapper applied on save — keeps plain emails looking like a normal message. */
const WRAP_OPEN =
  '<div style="font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1f2937;max-width:640px;margin:0 auto;padding:24px;">'
const WRAP_CLOSE = '</div>'

// Email-safe font stacks (widely rendered across mail clients).
const FONTS: { label: string; css: string }[] = [
  { label: 'Default', css: "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  { label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', css: "Georgia, 'Times New Roman', serif" },
  { label: 'Times', css: "'Times New Roman', Times, serif" },
  { label: 'Courier', css: "'Courier New', Courier, monospace" },
  { label: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', css: 'Tahoma, Geneva, sans-serif' },
  { label: 'Trebuchet', css: "'Trebuchet MS', Helvetica, sans-serif" },
]

// Legacy execCommand fontSize values 1–7; with styleWithCSS on these become
// CSS keyword sizes (small/medium/large/x-large…), which email clients honour.
const SIZES: { label: string; v: string }[] = [
  { label: 'Small', v: '2' },
  { label: 'Normal', v: '3' },
  { label: 'Large', v: '4' },
  { label: 'X-Large', v: '5' },
  { label: 'Huge', v: '6' },
]

const SWATCHES = ['#1f2937', '#6b7280', '#a3242b', '#1d4ed8', '#047857', '#b45309']

const MERGE_TAGS = [
  { label: 'First name', value: '{{firstName}}' },
  { label: 'Last name', value: '{{lastName}}' },
  { label: 'Their company', value: '{{company}}' },
  { label: 'Your company', value: '{{companyName}}' },
  { label: 'Your address', value: '{{companyAddress}}' },
  { label: 'Your website', value: '{{companyWebsite}}' },
]

/** Strip our save-time wrapper so editing starts from the inner content. */
function unwrap(html: string): string {
  const trimmed = (html || '').trim()
  if (trimmed.startsWith(WRAP_OPEN) && trimmed.endsWith(WRAP_CLOSE)) {
    return trimmed.slice(WRAP_OPEN.length, -WRAP_CLOSE.length)
  }
  return trimmed
}

export default function SimpleEmailEditor({ initialHtml, identity, onChange, hidePreview }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const onChangeRef = useRef(onChange)
  const loadedRef = useRef(false)
  const [showTags, setShowTags] = useState(false)
  const [preview, setPreview] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')

  useEffect(() => { onChangeRef.current = onChange })

  // Load once — writing innerHTML on every render would fight the caret.
  useEffect(() => {
    if (loadedRef.current || !ref.current) return
    // Make Enter create <p> (not the browser-default <div>), so paragraphs get
    // the .simple-editor spacing and a single Enter gives a real gap.
    try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch { /* older browsers */ }
    ref.current.innerHTML = unwrap(initialHtml ?? '') || '<p><br/></p>'
    loadedRef.current = true
  }, [initialHtml])

  const emit = useCallback(() => {
    if (!ref.current) return
    const inner = ref.current.innerHTML
    onChangeRef.current(inner.trim() ? WRAP_OPEN + inner + WRAP_CLOSE : '')
  }, [])

  // document.execCommand is deprecated but remains the only cross-browser way to
  // do rich-text editing without pulling in a full editor framework.
  const exec = useCallback((cmd: string, value?: string) => {
    ref.current?.focus()
    document.execCommand(cmd, false, value)
    emit()
  }, [emit])

  // For colour/font/size we want inline CSS (email-safe spans) rather than
  // legacy <font> tags, so flip styleWithCSS on around the command.
  const execStyled = useCallback((cmd: string, value: string) => {
    ref.current?.focus()
    try { document.execCommand('styleWithCSS', false, 'true') } catch { /* older browsers */ }
    document.execCommand(cmd, false, value)
    try { document.execCommand('styleWithCSS', false, 'false') } catch { /* older browsers */ }
    emit()
  }, [emit])

  const insertText = useCallback((text: string) => {
    ref.current?.focus()
    document.execCommand('insertText', false, text)
    emit()
  }, [emit])

  const addLink = useCallback(() => {
    const url = window.prompt('Link URL', 'https://')
    if (!url) return
    exec('createLink', url)
  }, [exec])

  // Track a clicked <img> so it can be re-linked / resized without re-inserting.
  const selectedImg = useRef<HTMLImageElement | null>(null)
  const [imgSel, setImgSel] = useState(false)
  const onEditorClick = useCallback((e: React.MouseEvent) => {
    const t = e.target as HTMLElement
    if (t && t.tagName === 'IMG') { selectedImg.current = t as HTMLImageElement; setImgSel(true) }
    else { selectedImg.current = null; setImgSel(false) }
  }, [])
  const editImage = useCallback(() => {
    const img = selectedImg.current
    if (!img) return
    const parent = img.parentElement
    const anchored = parent && parent.tagName === 'A' ? parent as HTMLAnchorElement : null
    const link = (window.prompt('Link this image to a URL? (blank to remove the link)', anchored?.getAttribute('href') || 'https://') || '').trim()
    const curW = img.getAttribute('width') || ''
    const wIn = (window.prompt('Image width in pixels? (blank = original)', curW) || '').trim()
    if (/^\d+$/.test(wIn)) img.setAttribute('width', wIn); else img.removeAttribute('width')
    if (link && link !== 'https://') {
      if (anchored) anchored.setAttribute('href', link)
      else { const a = document.createElement('a'); a.setAttribute('href', link); a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noreferrer'); img.replaceWith(a); a.appendChild(img) }
    } else if (anchored) {
      anchored.replaceWith(img)   // remove the link, keep the image
    }
    emit()
  }, [emit])

  // Upload an image to Supabase Storage and insert it at the caret.
  const imgInputRef = useRef<HTMLInputElement>(null)
  const insertImage = useCallback(async (file: File) => {
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase()
      const path = `${crypto.randomUUID()}.${ext}`
      const { error } = await supabase.storage.from('email-assets').upload(path, file, { cacheControl: '31536000', upsert: false })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('email-assets').getPublicUrl(path)
      // Optionally make the image a clickable link (e.g. a logo → your website).
      const link = (window.prompt('Link this image to a URL? (leave blank for none)', 'https://') || '').trim()
      const img = `<img src="${publicUrl}" alt="" style="max-width:100%;height:auto;border:0;" />`
      const wOpt = window.prompt('Image width in pixels? (leave blank for original)', '') || ''
      const w = /^\d+$/.test(wOpt.trim()) ? ` width="${wOpt.trim()}"` : ''
      const imgSized = w ? `<img src="${publicUrl}" alt=""${w} style="max-width:100%;height:auto;border:0;" />` : img
      const htmlToInsert = (link && link !== 'https://') ? `<a href="${link}" target="_blank" rel="noreferrer">${imgSized}</a>` : imgSized
      ref.current?.focus()
      document.execCommand('insertHTML', false, htmlToInsert + '&nbsp;')
      emit()
    } catch (e) { alert('Image upload failed: ' + (e instanceof Error ? e.message : 'unknown error')) }
  }, [emit])

  // Append the signature + disclaimer at the end, not at the caret — that's
  // where it belongs, and inserting mid-sentence is never what you meant.
  const addSignature = useCallback(() => {
    if (!ref.current) return
    ref.current.innerHTML = ref.current.innerHTML + PLAIN_SIGNATURE_HTML
    emit()
  }, [emit])

  const addUnsubscribe = useCallback(() => {
    ref.current?.focus()
    document.execCommand(
      'insertHTML', false,
      '<a href="{{UNSUBSCRIBE_URL}}" style="color:#6b7280;">Unsubscribe</a>',
    )
    emit()
  }, [emit])

  // Paste as plain text — pasting from Word/Outlook otherwise drags in styling
  // that renders badly in email clients.
  const onPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
    emit()
  }, [emit])

  /** Render what a recipient sees — merge tags filled from the sending profile. */
  const openPreview = useCallback(() => {
    const inner = ref.current?.innerHTML ?? ''
    setPreviewHtml(
      inner
        .replace(/\{\{companyName\}\}/g, identity?.companyName || '(set footer identity)')
        .replace(/\{\{companyAddress\}\}/g, identity?.companyAddress || '(set postal address)')
        .replace(/\{\{companyWebsite\}\}/g, identity?.companyWebsite || '(set website)')
        .replace(/\{\{firstName\}\}/g, 'Jane')
        .replace(/\{\{lastName\}\}/g, 'Smith')
        .replace(/\{\{company\}\}/g, 'Acme Ltd')
        .replace(/\{\{UNSUBSCRIBE_URL\}\}/g, '#preview'),
    )
    setPreview(true)
  }, [identity])

  const Btn = ({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) => (
    <button
      type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} title={title}
      className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
    >
      {children}
    </button>
  )

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex-wrap">
        <Btn onClick={() => exec('bold')} title="Bold (Ctrl+B)"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('italic')} title="Italic (Ctrl+I)"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('underline')} title="Underline (Ctrl+U)"><Underline className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Font family */}
        <select
          title="Font"
          defaultValue=""
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { if (e.target.value) execStyled('fontName', e.target.value); e.target.selectedIndex = 0 }}
          className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white text-gray-600 max-w-[7rem]"
        >
          <option value="">Font</option>
          {FONTS.map(f => <option key={f.css} value={f.css}>{f.label}</option>)}
        </select>

        {/* Font size (legacy 1-7 → CSS keyword when styleWithCSS is on) */}
        <select
          title="Text size"
          defaultValue=""
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { if (e.target.value) execStyled('fontSize', e.target.value); e.target.selectedIndex = 0 }}
          className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white text-gray-600"
        >
          <option value="">Size</option>
          {SIZES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>

        {/* Text colour: swatches + custom picker */}
        <div className="flex items-center gap-0.5">
          {SWATCHES.map(c => (
            <button
              key={c} type="button" title={`Text colour ${c}`}
              onMouseDown={e => e.preventDefault()}
              onClick={() => execStyled('foreColor', c)}
              className="w-4 h-4 rounded-full border border-gray-300"
              style={{ backgroundColor: c }}
            />
          ))}
          <label title="Custom colour" className="w-4 h-4 rounded-full border border-gray-300 overflow-hidden cursor-pointer"
            style={{ background: 'conic-gradient(red, orange, yellow, lime, cyan, blue, magenta, red)' }}>
            <input type="color" className="opacity-0 w-0 h-0"
              onChange={e => execStyled('foreColor', e.target.value)} />
          </label>
        </div>

        <div className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={() => exec('formatBlock', '<h2>')} title="Heading"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('insertUnorderedList')} title="Bulleted list"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={addLink} title="Insert link"><Link2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => imgInputRef.current?.click()} title="Insert image"><ImageIcon className="w-4 h-4" /></Btn>
        {imgSel && (
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={editImage} title="Edit selected image (link / size)"
            className="px-2 py-1 rounded-md text-[11px] font-medium text-brand-700 bg-brand-50 hover:bg-brand-100">Edit image</button>
        )}
        <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) insertImage(f); e.target.value = '' }} />

        <div className="relative">
          <Btn onClick={() => setShowTags(v => !v)} title="Insert merge tag"><Tag className="w-4 h-4" /></Btn>
          {showTags && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowTags(false)} />
              <div className="absolute left-0 top-full mt-1 z-20 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                {MERGE_TAGS.map(t => (
                  <button
                    key={t.value} type="button"
                    onClick={() => { insertText(t.value); setShowTags(false) }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex justify-between gap-2"
                  >
                    <span className="text-gray-700">{t.label}</span>
                    <span className="text-gray-400 font-mono">{t.value}</span>
                  </button>
                ))}
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    type="button"
                    onClick={() => { addUnsubscribe(); setShowTags(false) }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
                  >
                    Unsubscribe link
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <Btn onClick={addSignature} title="Append signature + disclaimer"><PenTool className="w-4 h-4" /></Btn>

        <div className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={() => exec('undo')} title="Undo"><Undo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('redo')} title="Redo"><Redo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('removeFormat')} title="Clear formatting"><Eraser className="w-4 h-4" /></Btn>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-gray-400 hidden lg:block">Pastes as plain text</span>
          {!hidePreview && (
            <button
              type="button"
              onClick={() => (preview ? setPreview(false) : openPreview())}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                preview
                  ? 'bg-brand-50 text-brand-700 border-brand-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              {preview ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {preview ? 'Edit' : 'Preview'}
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-2">
          <p className="text-[11px] text-gray-500">
            Preview — merge tags filled from the sending profile. The saved email keeps the tags,
            so changing the profile updates every email automatically.
          </p>
        </div>
      )}

      <div
        className={`simple-editor min-h-[420px] max-h-[70vh] overflow-y-auto px-6 py-5 text-[15px] leading-relaxed text-gray-800 ${preview ? '' : 'hidden'}`}
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onClick={onEditorClick}
        className={`simple-editor min-h-[420px] max-h-[70vh] overflow-y-auto px-6 py-5 outline-none text-[15px] leading-relaxed text-gray-800 ${preview ? 'hidden' : ''}`}
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
      />
    </div>
  )
}
